// src/base/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/base/button.tsx
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:opacity-90",
        secondary: "bg-secondary text-white hover:opacity-90",
        outline: "border-2 border-primary text-primary hover:bg-primary hover:text-white",
        ghost: "hover:bg-primary/10 text-primary",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/base/card.tsx
import * as React2 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Card = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border bg-surface text-text shadow-sm",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "h3",
  {
    ref,
    className: cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "p",
  {
    ref,
    className: cn("text-sm text-text-muted", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";

// src/base/dialog.tsx
import * as React3 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React3.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(DialogPortal, { children: [
  /* @__PURE__ */ jsx3(DialogOverlay, {}),
  /* @__PURE__ */ jsx3(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-surface p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg",
        className
      ),
      ...props,
      children
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx3(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx3(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-text-muted", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/base/input.tsx
import * as React4 from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Input = React4.forwardRef(
  ({ className, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx4(
      "input",
      {
        type,
        className: cn(
          "flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/base/select.tsx
import * as React5 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React5.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs2(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx5(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx5(
        "svg",
        {
          width: "15",
          height: "15",
          viewBox: "0 0 15 15",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          className: "h-4 w-4 opacity-50",
          children: /* @__PURE__ */ jsx5(
            "path",
            {
              d: "M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z",
              fill: "currentColor",
              fillRule: "evenodd",
              clipRule: "evenodd"
            }
          )
        }
      ) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectContent = React5.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx5(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsx5(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-text shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: /* @__PURE__ */ jsx5(
      SelectPrimitive.Viewport,
      {
        className: cn(
          "p-1",
          position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        ),
        children
      }
    )
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectItem = React5.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs2(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-primary/10 focus:text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx5("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx5(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx5(
        "svg",
        {
          width: "15",
          height: "15",
          viewBox: "0 0 15 15",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          className: "h-4 w-4",
          children: /* @__PURE__ */ jsx5(
            "path",
            {
              d: "M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z",
              fill: "currentColor",
              fillRule: "evenodd",
              clipRule: "evenodd"
            }
          )
        }
      ) }) }),
      /* @__PURE__ */ jsx5(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

// src/base/tabs.tsx
import * as React6 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx6 } from "react/jsx-runtime";
var Tabs = TabsPrimitive.Root;
var TabsList = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx6(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-text-muted",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx6(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx6(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// src/booth/booth-button.tsx
import * as React7 from "react";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var boothButtonVariants = cva2(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full text-lg font-bold transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/50 disabled:pointer-events-none disabled:opacity-50 active:scale-95 shadow-cute hover:shadow-cute-lg",
  {
    variants: {
      variant: {
        primary: "bg-primary text-white hover:brightness-110",
        secondary: "bg-secondary text-white hover:brightness-110",
        accent: "bg-accent text-white hover:brightness-110",
        outline: "border-4 border-primary text-primary bg-white hover:bg-primary hover:text-white",
        soft: "bg-primary/10 text-primary hover:bg-primary/20",
        ghost: "hover:bg-primary/10 text-primary"
      },
      size: {
        default: "h-14 px-8 py-3",
        sm: "h-12 px-6 py-2 text-base",
        lg: "h-16 px-10 py-4 text-xl",
        xl: "h-20 px-12 py-5 text-2xl",
        icon: "h-14 w-14"
      },
      animation: {
        none: "",
        bounce: "hover:animate-bounce-slow",
        pulse: "animate-pulse-slow",
        wiggle: "hover:animate-wiggle"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      animation: "none"
    }
  }
);
var BoothButton = React7.forwardRef(
  ({ className, variant, size, animation, icon, iconPosition = "left", children, ...props }, ref) => {
    return /* @__PURE__ */ jsxs3(
      "button",
      {
        className: cn(boothButtonVariants({ variant, size, animation, className })),
        ref,
        ...props,
        children: [
          icon && iconPosition === "left" && /* @__PURE__ */ jsx7("span", { className: "mr-2", children: icon }),
          children,
          icon && iconPosition === "right" && /* @__PURE__ */ jsx7("span", { className: "ml-2", children: icon })
        ]
      }
    );
  }
);
BoothButton.displayName = "BoothButton";

// src/booth/booth-card.tsx
import * as React8 from "react";
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx8 } from "react/jsx-runtime";
var boothCardVariants = cva3(
  "rounded-3xl bg-surface text-text transition-all duration-300",
  {
    variants: {
      variant: {
        default: "border-2 border-gray-200 shadow-cute hover:shadow-cute-lg",
        primary: "bg-primary text-white shadow-cute-lg",
        elevated: "shadow-cute-lg hover:shadow-2xl hover:-translate-y-1",
        outlined: "border-4 border-primary",
        soft: "bg-primary/5 border-2 border-primary/20"
      },
      padding: {
        default: "p-6",
        sm: "p-4",
        lg: "p-8",
        xl: "p-10"
      }
    },
    defaultVariants: {
      variant: "default",
      padding: "default"
    }
  }
);
var BoothCard = React8.forwardRef(
  ({ className, variant, padding, interactive, ...props }, ref) => /* @__PURE__ */ jsx8(
    "div",
    {
      ref,
      className: cn(
        boothCardVariants({ variant, padding }),
        interactive && "cursor-pointer hover:scale-105 active:scale-95",
        className
      ),
      ...props
    }
  )
);
BoothCard.displayName = "BoothCard";
var BoothCardHeader = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-2 mb-4", className),
    ...props
  }
));
BoothCardHeader.displayName = "BoothCardHeader";
var BoothCardTitle = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  "h3",
  {
    ref,
    className: cn(
      "text-3xl font-bold leading-none tracking-tight font-heading",
      className
    ),
    ...props
  }
));
BoothCardTitle.displayName = "BoothCardTitle";
var BoothCardDescription = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  "p",
  {
    ref,
    className: cn("text-lg text-text-muted", className),
    ...props
  }
));
BoothCardDescription.displayName = "BoothCardDescription";
var BoothCardContent = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8("div", { ref, className: cn("", className), ...props }));
BoothCardContent.displayName = "BoothCardContent";
var BoothCardFooter = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  "div",
  {
    ref,
    className: cn("flex items-center mt-4", className),
    ...props
  }
));
BoothCardFooter.displayName = "BoothCardFooter";

// src/booth/booth-dialog.tsx
import * as React9 from "react";
import * as DialogPrimitive2 from "@radix-ui/react-dialog";
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var BoothDialog = DialogPrimitive2.Root;
var BoothDialogTrigger = DialogPrimitive2.Trigger;
var BoothDialogPortal = DialogPrimitive2.Portal;
var BoothDialogClose = DialogPrimitive2.Close;
var BoothDialogOverlay = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  DialogPrimitive2.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
BoothDialogOverlay.displayName = DialogPrimitive2.Overlay.displayName;
var BoothDialogContent = React9.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(BoothDialogPortal, { children: [
  /* @__PURE__ */ jsx9(BoothDialogOverlay, {}),
  /* @__PURE__ */ jsx9(
    DialogPrimitive2.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-6 border-4 border-primary bg-surface p-8 shadow-cute-lg duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-3xl",
        className
      ),
      ...props,
      children
    }
  )
] }));
BoothDialogContent.displayName = DialogPrimitive2.Content.displayName;
var BoothDialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx9(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center",
      className
    ),
    ...props
  }
);
BoothDialogHeader.displayName = "BoothDialogHeader";
var BoothDialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx9(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-center sm:space-x-4",
      className
    ),
    ...props
  }
);
BoothDialogFooter.displayName = "BoothDialogFooter";
var BoothDialogTitle = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  DialogPrimitive2.Title,
  {
    ref,
    className: cn(
      "text-4xl font-bold leading-none tracking-tight font-heading text-primary",
      className
    ),
    ...props
  }
));
BoothDialogTitle.displayName = DialogPrimitive2.Title.displayName;
var BoothDialogDescription = React9.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
  DialogPrimitive2.Description,
  {
    ref,
    className: cn("text-xl text-text-muted", className),
    ...props
  }
));
BoothDialogDescription.displayName = DialogPrimitive2.Description.displayName;

// src/booth/booth-input.tsx
import * as React10 from "react";
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var boothInputVariants = cva4(
  "flex w-full rounded-2xl border-3 bg-white px-6 py-4 text-lg font-medium ring-offset-background transition-all duration-200 file:border-0 file:bg-transparent file:text-lg file:font-medium placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "border-gray-300",
        cute: "border-primary/30 shadow-cute focus-visible:shadow-cute-lg",
        primary: "border-primary/50 bg-primary/5"
      },
      inputSize: {
        default: "h-14",
        sm: "h-12 text-base px-4 py-3",
        lg: "h-16 text-xl px-8 py-5"
      }
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default"
    }
  }
);
var BoothInput = React10.forwardRef(
  ({ className, variant, inputSize, label, error, icon, ...props }, ref) => {
    return /* @__PURE__ */ jsxs5("div", { className: "w-full space-y-2", children: [
      label && /* @__PURE__ */ jsx10("label", { className: "text-lg font-semibold text-text block", children: label }),
      /* @__PURE__ */ jsxs5("div", { className: "relative", children: [
        icon && /* @__PURE__ */ jsx10("div", { className: "absolute left-4 top-1/2 -translate-y-1/2 text-text-muted", children: icon }),
        /* @__PURE__ */ jsx10(
          "input",
          {
            className: cn(
              boothInputVariants({ variant, inputSize }),
              icon && "pl-12",
              error && "border-red-500 focus-visible:ring-red-500/30",
              className
            ),
            ref,
            ...props
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx10("p", { className: "text-sm font-medium text-red-500", children: error })
    ] });
  }
);
BoothInput.displayName = "BoothInput";

// src/booth/booth-select.tsx
import * as React11 from "react";
import * as SelectPrimitive2 from "@radix-ui/react-select";
import { cva as cva5 } from "class-variance-authority";
import { jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
var boothSelectTriggerVariants = cva5(
  "flex w-full items-center justify-between rounded-2xl border-3 bg-white px-6 py-4 text-lg font-medium ring-offset-background transition-all duration-200 placeholder:text-text-muted focus:outline-none focus:ring-4 focus:ring-primary/30 focus:border-primary disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "border-gray-300",
        cute: "border-primary/30 shadow-cute"
      },
      selectSize: {
        default: "h-14",
        sm: "h-12 text-base px-4 py-3",
        lg: "h-16 text-xl px-8 py-5"
      }
    },
    defaultVariants: {
      variant: "default",
      selectSize: "default"
    }
  }
);
var BoothSelect = SelectPrimitive2.Root;
var BoothSelectGroup = SelectPrimitive2.Group;
var BoothSelectValue = SelectPrimitive2.Value;
var BoothSelectTrigger = React11.forwardRef(({ className, variant, selectSize, label, children, ...props }, ref) => /* @__PURE__ */ jsxs6("div", { className: "w-full space-y-2", children: [
  label && /* @__PURE__ */ jsx11("label", { className: "text-lg font-semibold text-text block", children: label }),
  /* @__PURE__ */ jsxs6(
    SelectPrimitive2.Trigger,
    {
      ref,
      className: cn(boothSelectTriggerVariants({ variant, selectSize, className })),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx11(SelectPrimitive2.Icon, { asChild: true, children: /* @__PURE__ */ jsx11(
          "svg",
          {
            width: "20",
            height: "20",
            viewBox: "0 0 15 15",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            className: "h-6 w-6 opacity-50",
            children: /* @__PURE__ */ jsx11(
              "path",
              {
                d: "M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z",
                fill: "currentColor",
                fillRule: "evenodd",
                clipRule: "evenodd"
              }
            )
          }
        ) })
      ]
    }
  )
] }));
BoothSelectTrigger.displayName = SelectPrimitive2.Trigger.displayName;
var BoothSelectContent = React11.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx11(SelectPrimitive2.Portal, { children: /* @__PURE__ */ jsx11(
  SelectPrimitive2.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-2xl border-3 border-primary/30 bg-white text-text shadow-cute-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: /* @__PURE__ */ jsx11(
      SelectPrimitive2.Viewport,
      {
        className: cn(
          "p-2",
          position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        ),
        children
      }
    )
  }
) }));
BoothSelectContent.displayName = SelectPrimitive2.Content.displayName;
var BoothSelectItem = React11.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs6(
  SelectPrimitive2.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-xl py-3 pl-10 pr-3 text-lg font-medium outline-none transition-colors focus:bg-primary/10 focus:text-primary data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx11("span", { className: "absolute left-3 flex h-5 w-5 items-center justify-center", children: /* @__PURE__ */ jsx11(SelectPrimitive2.ItemIndicator, { children: /* @__PURE__ */ jsx11(
        "svg",
        {
          width: "20",
          height: "20",
          viewBox: "0 0 15 15",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          className: "h-5 w-5",
          children: /* @__PURE__ */ jsx11(
            "path",
            {
              d: "M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z",
              fill: "currentColor",
              fillRule: "evenodd",
              clipRule: "evenodd"
            }
          )
        }
      ) }) }),
      /* @__PURE__ */ jsx11(SelectPrimitive2.ItemText, { children })
    ]
  }
));
BoothSelectItem.displayName = SelectPrimitive2.Item.displayName;

// src/booth/booth-tabs.tsx
import * as React12 from "react";
import * as TabsPrimitive2 from "@radix-ui/react-tabs";
import { jsx as jsx12 } from "react/jsx-runtime";
var BoothTabs = TabsPrimitive2.Root;
var BoothTabsList = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  TabsPrimitive2.List,
  {
    ref,
    className: cn(
      "inline-flex h-16 items-center justify-center rounded-full bg-primary/10 p-2 text-text-muted gap-2",
      className
    ),
    ...props
  }
));
BoothTabsList.displayName = TabsPrimitive2.List.displayName;
var BoothTabsTrigger = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  TabsPrimitive2.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 py-3 text-lg font-bold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-cute hover:bg-white/50",
      className
    ),
    ...props
  }
));
BoothTabsTrigger.displayName = TabsPrimitive2.Trigger.displayName;
var BoothTabsContent = React12.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  TabsPrimitive2.Content,
  {
    ref,
    className: cn(
      "mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
BoothTabsContent.displayName = TabsPrimitive2.Content.displayName;

// src/booth/booth-countdown.tsx
import * as React13 from "react";
import { jsx as jsx13 } from "react/jsx-runtime";
var sizeClasses = {
  default: "text-8xl",
  lg: "text-9xl",
  xl: "text-[12rem]"
};
var BoothCountdown = ({
  count,
  onComplete,
  className,
  size = "default"
}) => {
  const [currentCount, setCurrentCount] = React13.useState(count);
  const [isAnimating, setIsAnimating] = React13.useState(false);
  React13.useEffect(() => {
    setCurrentCount(count);
  }, [count]);
  React13.useEffect(() => {
    if (currentCount <= 0) {
      onComplete?.();
      return;
    }
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setCurrentCount((prev) => prev - 1);
      setIsAnimating(false);
    }, 1e3);
    return () => clearTimeout(timer);
  }, [currentCount, onComplete]);
  if (currentCount <= 0) {
    return /* @__PURE__ */ jsx13(
      "div",
      {
        className: cn(
          "flex items-center justify-center font-display font-bold text-primary animate-pulse",
          sizeClasses[size],
          className
        ),
        children: "\u{1F4F8}"
      }
    );
  }
  return /* @__PURE__ */ jsx13(
    "div",
    {
      className: cn(
        "flex items-center justify-center font-display font-bold text-primary transition-all duration-300",
        sizeClasses[size],
        isAnimating && "scale-125 animate-bounce",
        className
      ),
      children: currentCount
    }
  );
};
BoothCountdown.displayName = "BoothCountdown";

// src/booth/booth-photo-grid.tsx
import { jsx as jsx14, jsxs as jsxs7 } from "react/jsx-runtime";
var columnClasses = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4"
};
var BoothPhotoGrid = ({
  photos,
  onPhotoClick,
  columns = 3,
  selectable = false,
  className
}) => {
  return /* @__PURE__ */ jsx14(
    "div",
    {
      className: cn(
        "grid gap-4",
        columnClasses[columns],
        className
      ),
      children: photos.map((photo) => /* @__PURE__ */ jsxs7(
        "div",
        {
          className: cn(
            "relative aspect-square overflow-hidden rounded-2xl border-4 transition-all duration-300 cursor-pointer group",
            photo.selected ? "border-primary shadow-cute-lg scale-95" : "border-gray-200 hover:border-primary/50 hover:shadow-cute",
            selectable && "hover:scale-105 active:scale-95"
          ),
          onClick: () => onPhotoClick?.(photo),
          children: [
            /* @__PURE__ */ jsx14(
              "img",
              {
                src: photo.thumbnail || photo.url,
                alt: `Photo ${photo.id}`,
                className: "w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              }
            ),
            photo.selected && /* @__PURE__ */ jsx14("div", { className: "absolute inset-0 bg-primary/20 flex items-center justify-center", children: /* @__PURE__ */ jsx14("div", { className: "w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-cute-lg", children: /* @__PURE__ */ jsx14(
              "svg",
              {
                width: "24",
                height: "24",
                viewBox: "0 0 15 15",
                fill: "none",
                xmlns: "http://www.w3.org/2000/svg",
                children: /* @__PURE__ */ jsx14(
                  "path",
                  {
                    d: "M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z",
                    fill: "currentColor",
                    fillRule: "evenodd",
                    clipRule: "evenodd"
                  }
                )
              }
            ) }) })
          ]
        },
        photo.id
      ))
    }
  );
};
BoothPhotoGrid.displayName = "BoothPhotoGrid";

// src/booth/booth-frame-picker.tsx
import { jsx as jsx15, jsxs as jsxs8 } from "react/jsx-runtime";
var columnClasses2 = {
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5"
};
var BoothFramePicker = ({
  frames,
  selectedFrameId,
  onFrameSelect,
  columns = 4,
  className
}) => {
  return /* @__PURE__ */ jsxs8("div", { className: cn("space-y-4", className), children: [
    /* @__PURE__ */ jsx15(
      "div",
      {
        className: cn(
          "grid gap-4",
          columnClasses2[columns]
        ),
        children: frames.map((frame) => {
          const isSelected = frame.id === selectedFrameId;
          return /* @__PURE__ */ jsxs8(
            "div",
            {
              className: cn(
                "relative aspect-square overflow-hidden rounded-2xl border-4 transition-all duration-300 cursor-pointer group",
                isSelected ? "border-primary shadow-cute-lg scale-105" : "border-gray-200 hover:border-primary/50 hover:shadow-cute hover:scale-105",
                "active:scale-95"
              ),
              onClick: () => onFrameSelect?.(frame),
              children: [
                /* @__PURE__ */ jsx15(
                  "img",
                  {
                    src: frame.thumbnailUrl,
                    alt: frame.name,
                    className: "w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  }
                ),
                /* @__PURE__ */ jsx15("div", { className: "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3", children: /* @__PURE__ */ jsx15("p", { className: "text-white text-sm font-semibold truncate", children: frame.name }) }),
                isSelected && /* @__PURE__ */ jsx15("div", { className: "absolute top-2 right-2", children: /* @__PURE__ */ jsx15("div", { className: "w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow-cute", children: /* @__PURE__ */ jsx15(
                  "svg",
                  {
                    width: "16",
                    height: "16",
                    viewBox: "0 0 15 15",
                    fill: "none",
                    xmlns: "http://www.w3.org/2000/svg",
                    children: /* @__PURE__ */ jsx15(
                      "path",
                      {
                        d: "M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z",
                        fill: "currentColor",
                        fillRule: "evenodd",
                        clipRule: "evenodd"
                      }
                    )
                  }
                ) }) })
              ]
            },
            frame.id
          );
        })
      }
    ),
    frames.length === 0 && /* @__PURE__ */ jsxs8("div", { className: "text-center py-12", children: [
      /* @__PURE__ */ jsx15("p", { className: "text-2xl font-semibold text-text-muted", children: "No frames available" }),
      /* @__PURE__ */ jsx15("p", { className: "text-lg text-text-muted mt-2", children: "Check back later for new frames!" })
    ] })
  ] });
};
BoothFramePicker.displayName = "BoothFramePicker";
export {
  BoothButton,
  BoothCard,
  BoothCardContent,
  BoothCardDescription,
  BoothCardFooter,
  BoothCardHeader,
  BoothCardTitle,
  BoothCountdown,
  BoothDialog,
  BoothDialogClose,
  BoothDialogContent,
  BoothDialogDescription,
  BoothDialogFooter,
  BoothDialogHeader,
  BoothDialogOverlay,
  BoothDialogPortal,
  BoothDialogTitle,
  BoothDialogTrigger,
  BoothFramePicker,
  BoothInput,
  BoothPhotoGrid,
  BoothSelect,
  BoothSelectContent,
  BoothSelectGroup,
  BoothSelectItem,
  BoothSelectTrigger,
  BoothSelectValue,
  BoothTabs,
  BoothTabsContent,
  BoothTabsList,
  BoothTabsTrigger,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  boothButtonVariants,
  boothCardVariants,
  boothInputVariants,
  boothSelectTriggerVariants,
  buttonVariants,
  cn
};
